import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient, { formatApiError } from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Lightning, CheckCircle, Users, Storefront, Plus, Minus, Warning, CurrencyInr } = PhosphorIcons;
const RAZORPAY_KEY_ID = 'rzp_test_Sf25LOdqLnJ7jL';

const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [quote, setQuote] = useState(null);  // { preselect, minimum, actual_usage, pricing }
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [extraLocations, setExtraLocations] = useState(0);
  const [extraUsers, setExtraUsers] = useState(0);
  const [downgradeModal, setDowngradeModal] = useState(null);
  // Pro-rata add-on state
  const [addonPending, setAddonPending] = useState(null); // { type, qty, price, days }

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const fetchAll = async () => {
    try {
      const [infoRes, quoteRes] = await Promise.all([
        apiClient.get('/subscription/info'),
        apiClient.get('/subscription/renewal-quote').catch(() => ({ data: null })),
      ]);
      setInfo(infoRes.data);
      if (quoteRes?.data) {
        setQuote(quoteRes.data);
        // Pre-fill the renewal form with last cycle's choice (bumped to minimum if downgrade would be blocked)
        const preLoc = Math.max(0, (quoteRes.data.preselect?.locations || 1) - 1);
        const preUserAddon = Math.max(0, (quoteRes.data.preselect?.users || 2) - 2 - preLoc);
        setExtraLocations(preLoc);
        setExtraUsers(preUserAddon);
      }
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const basePlan = { users: 2, locations: 1, price: 499 };
  const totalLocations = basePlan.locations + extraLocations;
  const totalUsers = basePlan.users + extraLocations + extraUsers; // each extra location includes 1 user
  const totalPrice = basePlan.price + (extraLocations * 299) + (extraUsers * 99);

  // Minimums derived from live usage (server authoritative; UI just prevents bad selections pre-submit)
  const minLocAddon = Math.max(0, (quote?.actual_usage?.locations || 1) - 1);
  const minUserAddon = quote ? Math.max(0, (quote.minimum?.users || 2) - 2 - minLocAddon) : 0;

  const tryDecLoc = () => {
    if (extraLocations <= minLocAddon) {
      setDowngradeModal({
        type: 'locations',
        actual: quote?.actual_usage?.locations || 0,
        allowed: 1 + extraLocations,
        deleteTarget: '/dashboard/stores',
      });
      return;
    }
    setExtraLocations(Math.max(0, extraLocations - 1));
  };
  const tryDecUser = () => {
    if (extraUsers <= minUserAddon) {
      setDowngradeModal({
        type: 'users',
        actual: quote?.actual_usage?.users || 0,
        allowed: 2 + extraLocations + extraUsers,
        deleteTarget: '/dashboard/users',
      });
      return;
    }
    setExtraUsers(Math.max(0, extraUsers - 1));
  };

  const handleSubscribe = async (planType) => {
    // planType is either "trial" or "subscription"
    setUpgrading(true);
    try {
      const isTrial = planType === 'trial';
      const payload = isTrial
        ? { users: 2, locations: 1, plan_type: 'trial' }
        : { users: totalUsers, locations: totalLocations, plan_type: 'subscription' };
      const orderRes = await apiClient.post('/subscription/create-order', payload);
      const { order_id, amount, key_id } = orderRes.data;
      const options = {
        key: key_id || RAZORPAY_KEY_ID, amount, currency: 'INR',
        name: 'MusicSync',
        description: isTrial
          ? 'MusicSync 3-Day Trial'
          : `MusicSync Pro — ${totalLocations} location(s), ${totalUsers} user(s)`,
        order_id,
        handler: async (response) => {
          try {
            await apiClient.post('/subscription/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Subscription activated!');
            fetchAll();
          } catch (err) { toast.error('Payment verification failed'); }
        },
        prefill: { email: user?.email },
        theme: { color: '#2563eb' },
      };
      new window.Razorpay(options).open();
    } catch (error) {
      // Handle server-side downgrade guard
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && (detail.code === 'downgrade_blocked_locations' || detail.code === 'downgrade_blocked_users')) {
        setDowngradeModal({
          type: detail.code === 'downgrade_blocked_locations' ? 'locations' : 'users',
          actual: detail.actual_locations || detail.actual_users || 0,
          allowed: detail.required_locations_addon ? 1 + detail.required_locations_addon : 2 + minLocAddon + (detail.required_users_addon || 0),
          deleteTarget: detail.code === 'downgrade_blocked_locations' ? '/dashboard/stores' : '/dashboard/users',
          serverMessage: detail.message,
        });
      } else {
        toast.error(formatApiError(error));
      }
    }
    finally { setUpgrading(false); }
  };

  const buyAddon = async (type) => {
    // type = "location" | "user"
    try {
      const res = await apiClient.post('/subscription/addon-prorata', { addon_type: type, quantity: 1 });
      const { order_id, amount, key_id, line_item, days_remaining } = res.data;
      setAddonPending({ type, qty: 1, amount, days: days_remaining });
      const options = {
        key: key_id || RAZORPAY_KEY_ID,
        amount, currency: 'INR',
        name: 'MusicSync',
        description: line_item,
        order_id,
        handler: async (response) => {
          try {
            await apiClient.post('/subscription/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`${type === 'location' ? 'Location' : 'User'} add-on purchased`);
            setAddonPending(null);
            fetchAll();
          } catch (err) { toast.error('Payment verification failed'); setAddonPending(null); }
        },
        prefill: { email: user?.email },
        theme: { color: '#2563eb' },
      };
      new window.Razorpay(options).open();
    } catch (error) {
      setAddonPending(null);
      toast.error(formatApiError(error));
    }
  };

  if (loading) return <div className="text-[#8a8477] font-mono">Loading...</div>;

  const status = info?.status;
  const isActive = status === 'active' || status === 'trial';
  const isPending = status === 'pending_payment';
  const hasUsedTrial = info?.subscription?.end_date != null;
  const showTrial = isPending && !hasUsedTrial;
  const currentPlan = info?.subscription?.plan || {};

  return (
    <div className="space-y-6 max-w-3xl" data-testid="subscription-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-[#2d2a26] mb-2">Subscription</h1>
        <p className="text-[#8a8477] font-mono text-sm tracking-wider uppercase">Manage your plan</p>
      </div>

      {/* Current Status */}
      {info && (
        <div className={`rounded-xl p-5 border ${isActive ? 'bg-green-50 border-green-200' : isPending ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="subscription-status">
          <div className="flex items-center gap-3">
            <Lightning size={24} weight="fill" className={isActive ? 'text-green-600' : 'text-amber-600'} />
            <div>
              <p className="font-bold text-[#2d2a26]">
                {status === 'trial' ? 'Trial Period' : status === 'active' ? 'Active Subscription' : 'Subscription Expired'}
              </p>
              <p className="text-sm text-[#6b665c]">
                {currentPlan.users || 0} users, {currentPlan.locations || 0} location(s)
                {info?.subscription?.end_date && ` — expires ${new Date(info.subscription.end_date).toLocaleDateString()}`}
              </p>
            </div>
          </div>
          {info?.current_usage && (
            <div className="mt-3 flex gap-6 text-sm text-[#6b665c]">
              <span>Using: {info.current_usage.users}/{currentPlan.users || '?'} users</span>
              <span>{info.current_usage.locations}/{currentPlan.locations || '?'} locations</span>
            </div>
          )}
        </div>
      )}

      {/* Trial Card — only for first-time pending users */}
      {showTrial && (
        <div className="bg-[#fdf2f4] border-2 border-[#d4919a] rounded-xl p-6 shadow-sm" data-testid="trial-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#2d2a26]">3-Day Trial</h2>
              <p className="text-sm text-[#6b665c]">Try all features before committing</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-[#d4919a]">Rs. 59</span>
              <span className="text-[#8a8477] text-sm">/ 3 days</span>
            </div>
          </div>
          <ul className="space-y-2 text-sm text-[#6b665c] mb-5">
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#d4919a]" />All features unlocked</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#d4919a]" />2 users (1 admin + 1 kiosk)</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#d4919a]" />1 location included</li>
          </ul>
          <Button onClick={() => handleSubscribe('trial')} disabled={upgrading} className="w-full bg-[#d4919a] hover:bg-[#c07e87] text-white border-0 h-12 text-base font-medium" data-testid="trial-pay-button">
            {upgrading ? 'Processing...' : 'Activate Trial — Rs. 59'}
          </Button>
        </div>
      )}

      {/* Mid-cycle add-on (pro-rata) — only for active subscribers */}
      {isActive && !isPending && (
        <div className="bg-white border border-[#d9d3c5] rounded-xl p-5 shadow-sm" data-testid="prorata-addon-card">
          <div className="flex items-center gap-2 mb-3">
            <CurrencyInr size={18} className="text-emerald-600" />
            <h3 className="font-bold text-[#2d2a26] text-sm uppercase tracking-wider">Add-ons (pro-rated)</h3>
          </div>
          <p className="text-xs text-[#6b665c] mb-4">
            Buy an add-on right now; you'll be charged only for the days remaining in your current cycle. Full price kicks in on renewal.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between bg-[#f5f0e8] rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-[#2d2a26]">+ 1 Location</p>
                <p className="text-xs text-[#8a8477]">Rs. 299/month (includes 1 user)</p>
              </div>
              <Button onClick={() => buyAddon('location')} disabled={!!addonPending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="buy-location-addon">
                Buy
              </Button>
            </div>
            <div className="flex items-center justify-between bg-[#f5f0e8] rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-[#2d2a26]">+ 1 User</p>
                <p className="text-xs text-[#8a8477]">Rs. 99/month</p>
              </div>
              <Button onClick={() => buyAddon('user')} disabled={!!addonPending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="buy-user-addon">
                Buy
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Card */}
      <div className="bg-white border border-[#d9d3c5] rounded-xl overflow-hidden shadow-sm">
        <div className="bg-blue-600 p-6 text-white">
          <h2 className="text-xl font-bold mb-1">MusicSync Pro</h2>
          <p className="text-blue-100 text-sm">All features included — one simple plan</p>
        </div>
        <div className="p-6">
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-bold text-[#2d2a26]">Rs. {basePlan.price}</span>
            <span className="text-[#8a8477]">/month</span>
          </div>
          <ul className="space-y-2 text-sm text-[#6b665c] mb-6">
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />2 users (1 admin + 1 kiosk)</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />1 location</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />Google Drive integration</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />Dayparting & scheduling</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />Real-time remote control</li>
            <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-green-500" />Announcements & branding</li>
          </ul>

          {/* Add-ons */}
          <div className="border-t border-[#e8e3d8] pt-5 space-y-4">
            <h3 className="font-bold text-[#2d2a26] text-sm uppercase tracking-wider">Add-ons</h3>

            <div className="flex items-center justify-between bg-[#f5f0e8] rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-[#2d2a26]">Extra Location + 1 User</p>
                <p className="text-xs text-[#8a8477]">Rs. 299/month each</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={tryDecLoc} className="w-8 h-8 rounded-full bg-white border border-[#d9d3c5] flex items-center justify-center hover:bg-[#ede8dc]" data-testid="dec-locations"><Minus size={14} /></button>
                <span className="font-bold text-lg w-6 text-center text-[#2d2a26]">{extraLocations}</span>
                <button onClick={() => setExtraLocations(extraLocations + 1)} className="w-8 h-8 rounded-full bg-white border border-[#d9d3c5] flex items-center justify-center hover:bg-[#ede8dc]"><Plus size={14} /></button>
              </div>
            </div>

            <div className="flex items-center justify-between bg-[#f5f0e8] rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-[#2d2a26]">Extra User</p>
                <p className="text-xs text-[#8a8477]">Rs. 99/month each</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={tryDecUser} className="w-8 h-8 rounded-full bg-white border border-[#d9d3c5] flex items-center justify-center hover:bg-[#ede8dc]" data-testid="dec-users"><Minus size={14} /></button>
                <span className="font-bold text-lg w-6 text-center text-[#2d2a26]">{extraUsers}</span>
                <button onClick={() => setExtraUsers(extraUsers + 1)} className="w-8 h-8 rounded-full bg-white border border-[#d9d3c5] flex items-center justify-center hover:bg-[#ede8dc]"><Plus size={14} /></button>
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-[#6b665c]">{totalLocations} location(s), {totalUsers} user(s)</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">Rs. {totalPrice}</p>
                <p className="text-xs text-[#8a8477]">/month</p>
              </div>
            </div>
          </div>

          <Button onClick={() => handleSubscribe('subscription')} disabled={upgrading} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white border-0 h-12 text-base font-medium" data-testid="subscribe-button">
            {upgrading ? 'Processing...' : isActive ? 'Upgrade Plan' : 'Subscribe Now — Rs. ' + totalPrice}
          </Button>
        </div>
      </div>

      {/* Downgrade-blocked modal */}
      {downgradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="downgrade-blocked-modal"
          onClick={() => setDowngradeModal(null)}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Warning size={22} weight="fill" className="text-amber-600" />
              <h3 className="text-lg font-bold text-[#2d2a26]">Can't downgrade</h3>
            </div>
            <p className="text-sm text-[#6b665c] mb-4">
              {downgradeModal.serverMessage || (
                downgradeModal.type === 'locations'
                  ? `You currently have ${downgradeModal.actual} locations but this plan would only allow ${downgradeModal.allowed}. Please permanently delete ${downgradeModal.actual - downgradeModal.allowed} location${(downgradeModal.actual - downgradeModal.allowed) === 1 ? '' : 's'} before downgrading.`
                  : `You currently have ${downgradeModal.actual} users but this plan would only allow ${downgradeModal.allowed}. Please permanently delete ${downgradeModal.actual - downgradeModal.allowed} user${(downgradeModal.actual - downgradeModal.allowed) === 1 ? '' : 's'} before downgrading.`
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDowngradeModal(null)} data-testid="downgrade-modal-cancel">
                Go back
              </Button>
              <Button
                onClick={() => {
                  const target = downgradeModal.deleteTarget;
                  setDowngradeModal(null);
                  navigate(target);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="downgrade-modal-delete"
              >
                {downgradeModal.type === 'locations' ? 'Delete a location' : 'Delete a user'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
